import { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { useFileUpload } from "../../hooks/useFileUpload";
import { careService } from "../../services/careService";

const MONTHLY_MANUAL_FAX_LIMIT = 5;

const manualFaxUsageKey = (workspaceId, date = new Date()) =>
  `ocarely:efax:manual:${workspaceId}:${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const storedManualFaxUsage = (workspaceId) => {
  if (!workspaceId) return 0;
  const stored = Number(window.localStorage.getItem(manualFaxUsageKey(workspaceId)) || 0);
  return Number.isFinite(stored) ? Math.max(0, stored) : 0;
};

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const parseFaxNumbers = (...values) =>
  Array.from(
    new Set(
      values
        .flatMap((value) => String(value || "").split(/[\n,;]+/))
        .map((number) => number.trim())
        .filter(Boolean),
    ),
  );

const currentUserId = (user = {}) => user?.id || user?.userId || user?.uid || "";

const statusClass = (status = "") => {
  const normalized = String(status || "").toUpperCase();
  if (["SENT", "STORED", "RECEIVED"].includes(normalized)) return "low";
  if (normalized === "DELIVERED") return "low";
  if (["FAILED", "BUSY", "NO_ANSWER"].includes(normalized)) return "high";
  return "medium";
};

const formatDateTime = (value) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value >= 10 || unitIndex === 0 ? Math.round(value) : value.toFixed(1)} ${units[unitIndex]}`;
};

const DEFAULT_FAX_COUNTRY_CODE = "+1 ";

const isCompleteUsFaxNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1");
};

const defaultForm = () => ({
  faxNumber: DEFAULT_FAX_COUNTRY_CODE,
  faxNumbers: [DEFAULT_FAX_COUNTRY_CODE],
  fileId: "",
  signedUrl: "",
  fileName: "",
  textMessage: "",
});

const pdfSafeText = (value = "") =>
  String(value)
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, "-")
    .split("")
    .map((character) => {
      const code = character.charCodeAt(0);
      return code >= 32 && code <= 126 ? character : "?";
    })
    .join("")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const wrapText = (value, maxLength = 88) => {
  const lines = [];
  String(value || "").split(/\r?\n/).forEach((paragraph) => {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push("");
      return;
    }
    let line = "";
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (candidate.length > maxLength && line) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    });
    if (line) lines.push(line);
  });
  return lines;
};

const createTextFaxPdfFile = (text, requestedFileName) => {
  const fileName = firstText(requestedFileName, "fax-message.pdf").endsWith(".pdf")
    ? firstText(requestedFileName, "fax-message.pdf")
    : `${firstText(requestedFileName, "fax-message")}.pdf`;
  const createdAt = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());
  const lines = [
    "OCarely Fax Message",
    `Created: ${createdAt}`,
    "",
    ...wrapText(text),
  ];
  const linesPerPage = 43;
  const pages = [];
  for (let index = 0; index < lines.length; index += linesPerPage) {
    pages.push(lines.slice(index, index + linesPerPage));
  }

  const objects = [];
  const addObject = (body) => {
    objects.push(body);
    return objects.length;
  };
  const pageRefs = [];
  const catalogRef = addObject("<< /Type /Catalog /Pages 2 0 R >>");
  const pagesRef = addObject("");
  const fontRef = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");

  pages.forEach((pageLines) => {
    const content = [
      "BT",
      "/F1 11 Tf",
      "50 760 Td",
      "14 TL",
      ...pageLines.map((line) => `(${pdfSafeText(line)}) Tj T*`),
      "ET",
    ].join("\n");
    const contentRef = addObject(`<< /Length ${new TextEncoder().encode(content).length} >>\nstream\n${content}\nendstream`);
    const pageRef = addObject(`<< /Type /Page /Parent ${pagesRef} 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontRef} 0 R >> >> /Contents ${contentRef} 0 R >>`);
    pageRefs.push(pageRef);
  });

  objects[pagesRef - 1] = `<< /Type /Pages /Kids [${pageRefs.map((ref) => `${ref} 0 R`).join(" ")}] /Count ${pageRefs.length} >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((body, index) => {
    offsets.push(new TextEncoder().encode(pdf).length);
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = new TextEncoder().encode(pdf).length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogRef} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new File([new Blob([pdf], { type: "application/pdf" })], fileName, { type: "application/pdf" });
};

export default function EfaxPage() {
  const { user } = useAuth();
  const actorId = currentUserId(user);
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const { uploadFile, uploading } = useFileUpload();
  const [form, setForm] = useState(() => defaultForm());
  const [selectedFileName, setSelectedFileName] = useState("");
  const [sendMode, setSendMode] = useState("document");
  const [attachedDocument, setAttachedDocument] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [uploadError, setUploadError] = useState("");
  const [faxes, setFaxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [retryingFaxId, setRetryingFaxId] = useState("");
  const [refreshingFaxId, setRefreshingFaxId] = useState("");
  const [activeActivityTab, setActiveActivityTab] = useState(() => {
    const requestedTab = new URLSearchParams(window.location.search).get("tab");
    return ["outbox", "inbox", "send"].includes(requestedTab) ? requestedTab : "outbox";
  });
  const [error, setError] = useState("");
  const [sharedFaxNumber, setSharedFaxNumber] = useState("");
  const [manualFaxesSentThisMonth, setManualFaxesSentThisMonth] = useState(0);

  const workspaceParams = useMemo(
    () => ({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }),
    [selectedWorkspaceId],
  );

  const loadFaxes = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setFaxes([]);
      return;
    }
    setLoading(true);
    setError("");
    careService.efax.list(workspaceParams, actorId)
      .then((data) => setFaxes(pageItems(data)))
      .catch((err) => {
        setFaxes([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load fax records.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadFaxes();
  }, [selectedWorkspaceId, workspaceListReady, actorId]);

  useEffect(() => {
    setManualFaxesSentThisMonth(storedManualFaxUsage(selectedWorkspaceId));
  }, [selectedWorkspaceId]);

 
  useEffect(() => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setSharedFaxNumber("");
      return;
    }
    careService.efax
      .sharedNumber(workspaceParams, actorId)
      .then((data) => {
        const number = data?.efaxNumber ? String(data.efaxNumber) : "";
        setSharedFaxNumber(number);
      })
      .catch(() => setSharedFaxNumber(""));
  }, [selectedWorkspaceId, workspaceListReady, actorId]);


  const canSendFax = true;
  const inboxFaxes = useMemo(
    () => faxes.filter((fax) => String(fax.direction || "").toUpperCase() === "INBOUND"),
    [faxes],
  );
  const outboxFaxes = useMemo(
    () => faxes.filter((fax) => String(fax.direction || "").toUpperCase() !== "INBOUND"),
    [faxes],
  );
  const visibleFaxes = activeActivityTab === "inbox" ? inboxFaxes : outboxFaxes;
  const manualFaxesRemaining = Math.max(0, MONTHLY_MANUAL_FAX_LIMIT - manualFaxesSentThisMonth);

  const selectTab = (tab) => {
    setActiveActivityTab(tab);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const recordManualFaxUsage = (count) => {
    if (!selectedWorkspaceId || count <= 0) return;
    setManualFaxesSentThisMonth((current) => {
      const next = Math.min(MONTHLY_MANUAL_FAX_LIMIT, current + count);
      window.localStorage.setItem(manualFaxUsageKey(selectedWorkspaceId), String(next));
      return next;
    });
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateFaxRecipient = (index, value) => {
    setForm((current) => {
      const faxNumbers = [...(current.faxNumbers?.length ? current.faxNumbers : [current.faxNumber || ""])];
      faxNumbers[index] = value;
      return { ...current, faxNumber: faxNumbers[0] || "", faxNumbers };
    });
  };

  const addFaxRecipient = () => {
    setForm((current) => ({
      ...current,
      faxNumbers: [...(current.faxNumbers?.length ? current.faxNumbers : [current.faxNumber || DEFAULT_FAX_COUNTRY_CODE]), DEFAULT_FAX_COUNTRY_CODE],
    }));
  };

  const removeFaxRecipient = (index) => {
    setForm((current) => {
      const faxNumbers = (current.faxNumbers?.length ? current.faxNumbers : [current.faxNumber || ""]).filter((_, itemIndex) => itemIndex !== index);
      const nextFaxNumbers = faxNumbers.length ? faxNumbers : [DEFAULT_FAX_COUNTRY_CODE];
      return { ...current, faxNumber: nextFaxNumbers[0] || "", faxNumbers: nextFaxNumbers };
    });
  };

  const handleFaxRecipientPaste = (index, event) => {
    const pastedNumbers = parseFaxNumbers(event.clipboardData?.getData("text"));
    if (pastedNumbers.length <= 1) return;
    event.preventDefault();
    setForm((current) => {
      const currentNumbers = current.faxNumbers?.length ? current.faxNumbers : [current.faxNumber || ""];
      const nextFaxNumbers = [
        ...currentNumbers.slice(0, index),
        ...pastedNumbers,
        ...currentNumbers.slice(index + 1),
      ];
      return { ...current, faxNumber: nextFaxNumbers[0] || "", faxNumbers: nextFaxNumbers };
    });
  };

  const clearDocument = () => {
    setForm((current) => ({ ...current, fileId: "", signedUrl: "", fileName: sendMode === "text" ? current.fileName : "" }));
    setSelectedFileName("");
    setAttachedDocument(null);
    setUploadStatus("idle");
    setUploadError("");
  };

  const clearForm = () => {
    setForm(defaultForm());
    setSelectedFileName("");
    setAttachedDocument(null);
    setUploadStatus("idle");
    setUploadError("");
  };

  const switchSendMode = (mode) => {
    setSendMode(mode);
    clearDocument();
    setForm((current) => ({
      ...current,
      fileId: "",
      signedUrl: "",
      fileName: mode === "text" ? firstText(current.fileName, "fax-message.pdf") : "",
      textMessage: mode === "document" ? "" : current.textMessage,
    }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!actorId) {
      message.error("Your user profile is required before uploading a fax document.");
      return;
    }

    setSelectedFileName(file.name);
    setAttachedDocument({
      fileId: "",
      name: file.name,
      size: file.size,
      type: file.type,
    });
    setUploadStatus("uploading");
    setUploadError("");
    setError("");
    try {
      const upload = await uploadFile(actorId, file, {
        purpose: "OTHER",
        fileType: file.type || "DOCUMENT",
        preserveOriginal: true,
        confirmUpload: false,
      });
      const uploadedFileId = upload?.fileId || upload?.file?.id || "";
      if (!uploadedFileId) throw new Error("Storage upload did not return a file id.");
      setForm((current) => ({
        ...current,
        fileId: uploadedFileId,
        signedUrl: "",
        fileName: file.name,
      }));
      setAttachedDocument({
        fileId: uploadedFileId,
        name: file.name,
        size: file.size,
        type: file.type,
      });
      setUploadStatus("ready");
      message.success("Fax document uploaded.");
    } catch (err) {
      const fallbackMessage = err?.message || "Unable to upload fax document.";
      setForm((current) => ({ ...current, fileId: "", signedUrl: "" }));
      setUploadStatus("error");
      setUploadError(fallbackMessage);
      message.error(fallbackMessage);
    }
  };

  const uploadTextFaxDocument = async () => {
    const text = form.textMessage.trim();
    if (!text) {
      throw new Error("Type a message before sending a text fax.");
    }
    if (!actorId) {
      throw new Error("Your user profile is required before sending a text fax.");
    }
    const file = createTextFaxPdfFile(text, form.fileName);

    setSelectedFileName(file.name);
    setAttachedDocument({
      fileId: "",
      name: file.name,
      size: file.size,
      type: file.type,
    });
    setUploadStatus("uploading");
    setUploadError("");

    const upload = await uploadFile(actorId, file, {
      purpose: "OTHER",
      fileType: "application/pdf",
      preserveOriginal: true,
      confirmUpload: false,
    });
    const uploadedFileId = upload?.fileId || upload?.file?.id || "";
    if (!uploadedFileId) throw new Error("Storage upload did not return a file id.");
    setAttachedDocument({
      fileId: uploadedFileId,
      name: file.name,
      size: file.size,
      type: file.type,
    });
    setUploadStatus("ready");
    return { fileId: uploadedFileId, fileName: file.name };
  };

  const sendFax = async (event) => {
    event.preventDefault();
    if (!selectedWorkspaceId) {
      message.error("Select a workspace before sending a fax.");
      return;
    }
    const recipientNumbers = parseFaxNumbers(...(form.faxNumbers?.length ? form.faxNumbers : [form.faxNumber]));
    if (!recipientNumbers.length || recipientNumbers.every((number) => number.trim() === "+1")) {
      message.error("Enter at least one destination fax number.");
      return;
    }
    if (recipientNumbers.some((number) => !isCompleteUsFaxNumber(number))) {
      message.error("Enter a valid US fax number including the +1 country code.");
      return;
    }
    const destinations = recipientNumbers;
    if (manualFaxesRemaining === 0) {
      message.error("The monthly allowance of 5 manually sent faxes has been reached.");
      return;
    }
    if (destinations.length > manualFaxesRemaining) {
      message.error(`You can send ${manualFaxesRemaining} more fax${manualFaxesRemaining === 1 ? "" : "es"} this month.`);
      return;
    }
    if (sendMode === "document" && !form.fileId) {
      message.error("Upload a document before sending.");
      return;
    }
    if (sendMode === "text" && !form.textMessage.trim()) {
      message.error("Type a message before sending a text fax.");
      return;
    }

    setSending(true);
    try {
      const textUpload = sendMode === "text" ? await uploadTextFaxDocument() : null;
      const fileId = textUpload?.fileId || form.fileId;
      const fileName = textUpload?.fileName || firstText(form.fileName, selectedFileName, "fax-document");
      const results = await Promise.allSettled(
        destinations.map((faxNumber) =>
          careService.efax.send(
            {
              faxNumber,
              fileId,
              signedUrl: form.signedUrl,
              fileName,
              workspaceId: selectedWorkspaceId,
            },
            workspaceParams,
            actorId,
          ),
        ),
      );
      const sentFaxes = results
        .filter((result) => result.status === "fulfilled" && result.value)
        .map((result) => result.value);
      const rejected = results.filter((result) => result.status === "rejected");
      if (sentFaxes.length) {
        setFaxes((current) => [
          ...sentFaxes,
          ...current.filter((item) => !sentFaxes.some((sent) => sent?.id && sent.id === item.id)),
        ]);
      }
      const failedResponses = sentFaxes.filter((fax) => String(fax?.status || "").toUpperCase() === "FAILED");
      const acceptedManualSends = sentFaxes.length - failedResponses.length;
      recordManualFaxUsage(acceptedManualSends);
      if (rejected.length || failedResponses.length) {
        const firstError =
          rejected[0]?.reason?.response?.data?.message ||
          rejected[0]?.reason?.message ||
          failedResponses[0]?.failureReason ||
          "Some faxes could not be sent.";
        message.error(firstError);
      } else {
        clearForm();
        message.success(
          destinations.length === 1
            ? "Fax submitted to the OCarely shared number. It will appear in the inbox once received and routed."
            : `${destinations.length} faxes submitted.`,
        );
      }
    } catch (err) {
      const fallbackMessage = err?.response?.data?.message || err?.message || "Unable to send fax.";
      if (sendMode === "text") {
        setUploadStatus("error");
        setUploadError(fallbackMessage);
      }
      message.error(fallbackMessage);
    } finally {
      setSending(false);
    }
  };

  const retryFax = async (fax) => {
    if (!selectedWorkspaceId) {
      message.error("Select a workspace before retrying a fax.");
      return;
    }
    if (!fax?.storageFileId) {
      message.error("This fax cannot be retried because it does not have a stored document.");
      return;
    }
    if (manualFaxesRemaining === 0) {
      message.error("The monthly allowance of 5 manually sent faxes has been reached.");
      return;
    }
    setRetryingFaxId(fax.id || "");
    try {
      const retried = await careService.efax.send(
        {
          faxNumber: firstText(fax.toNumber),
          fileId: fax.storageFileId,
          signedUrl: firstText(fax.signedUrl),
          fileName: firstText(fax.fileName, "fax-document"),
          workspaceId: selectedWorkspaceId,
        },
        workspaceParams,
        actorId,
      );
      setFaxes((current) => [retried, ...current.filter((item) => item.id !== retried?.id)]);
      if (String(retried?.status || "").toUpperCase() === "FAILED") {
        message.error(retried?.failureReason || "Fax retry failed.");
      } else {
        recordManualFaxUsage(1);
        message.success("Fax retry submitted.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to retry fax.");
    } finally {
      setRetryingFaxId("");
    }
  };

  const refreshFaxStatus = async (fax) => {
    if (!selectedWorkspaceId) {
      message.error("Select a workspace before refreshing fax status.");
      return;
    }
    if (!fax?.id) {
      message.error("This fax cannot be refreshed because it does not have a local fax id.");
      return;
    }
    if (!fax?.vendorFaxId) {
      message.error("This fax cannot be refreshed because eFax did not return a provider fax id.");
      return;
    }
    setRefreshingFaxId(fax.id || "");
    try {
      const updated = await careService.efax.refreshStatus(fax.id, workspaceParams, actorId);
      setFaxes((current) => [updated, ...current.filter((item) => item.id !== updated?.id)]);
      if (String(updated?.status || "").toUpperCase() === "FAILED") {
        message.error(updated?.failureReason || "Fax delivery failed.");
      } else {
        message.success("Fax status refreshed.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to refresh fax status.");
    } finally {
      setRefreshingFaxId("");
    }
  };

  const renderFaxRows = () => {
    if (loading) {
      return (
        <div className="efax-activity-empty">
          <i className="ti ti-loader-2" aria-hidden="true" />
          <span>Loading fax activity...</span>
        </div>
      );
    }
    if (!visibleFaxes.length) {
      return (
        <div className="efax-activity-empty">
          <i className={`ti ${activeActivityTab === "inbox" ? "ti-inbox" : "ti-send"}`} aria-hidden="true" />
          <strong>{activeActivityTab === "inbox" ? "No inbound faxes" : "No outbound faxes"}</strong>
          <span>{activeActivityTab === "inbox" ? "Received faxes will appear here." : "Send a fax to begin building the activity history."}</span>
        </div>
      );
    }
    return visibleFaxes.map((fax) => {
      const direction = String(fax.direction || "").toUpperCase();
      const isInbound = direction === "INBOUND";
      const status = String(fax.status || "").toUpperCase();
      const number = isInbound
        ? firstText(fax.fromNumber, "Unknown sender")
        : firstText(fax.toNumber, "Unknown recipient");
      const canRetry = !isInbound && status === "FAILED" && fax.storageFileId;
      const canRefreshStatus = !isInbound && Boolean(fax.vendorFaxId) && status !== "FAILED";
      return (
        <article className="efax-activity-row" key={fax.id || `${direction}-${number}-${fax.createdAt}`}>
          <div className="efax-activity-party">
            <i className={`ti ${isInbound ? "ti-arrow-down-left" : "ti-arrow-up-right"}`} aria-hidden="true" />
            <div>
              <small>{isInbound ? "From" : "To"}</small>
              <strong>{number}</strong>
            </div>
          </div>
          <div className="efax-activity-document">
            <small>Document</small>
            {fax.signedUrl ? (
              <a href={fax.signedUrl} target="_blank" rel="noreferrer">
                <i className="ti ti-file-description" aria-hidden="true" />
                <span>{firstText(fax.fileName, "View document")}</span>
              </a>
            ) : <strong>{firstText(fax.fileName, "Document unavailable")}</strong>}
            {fax.failureReason ? <span className="efax-failure-text">{fax.failureReason}</span> : null}
          </div>
          <div className="efax-activity-status">
            <small>Status</small>
            <em className={`pill ${statusClass(fax.status)}`}>{firstText(fax.status, "UNKNOWN")}</em>
          </div>
          <div className="efax-activity-updated">
            <small>Updated</small>
            <strong>{formatDateTime(fax.updatedAt || fax.sentAt || fax.receivedAt || fax.createdAt)}</strong>
          </div>
          <div className="efax-activity-actions">
            {fax.signedUrl ? (
              <a className="mini-btn efax-row-action" href={fax.signedUrl} target="_blank" rel="noreferrer" aria-label="View fax document" title="View document">
                <i className="ti ti-eye" />
              </a>
            ) : null}
            {canRetry ? (
              <button
                className="mini-btn efax-row-action"
                type="button"
                onClick={() => retryFax(fax)}
                disabled={retryingFaxId === fax.id}
              >
                <i className="ti ti-refresh" /> {retryingFaxId === fax.id ? "Retrying..." : "Retry"}
              </button>
            ) : canRefreshStatus ? (
              <button
                className="mini-btn efax-row-action"
                type="button"
                onClick={() => refreshFaxStatus(fax)}
                disabled={refreshingFaxId === fax.id}
              >
                <i className="ti ti-refresh" /> {refreshingFaxId === fax.id ? "Checking..." : "Check Status"}
              </button>
            ) : (
              !fax.signedUrl ? <span className="efax-muted-action">No actions</span> : null
            )}
          </div>
        </article>
      );
    });
  };

  return (
    <CareShell className="dashboard-content efax-page">
      <header className="efax-page-header">
        <div className="page-title">
          <span className="efax-page-eyebrow">Secure document exchange</span>
          <h1>eFax</h1>
          <p>Send and receive workspace documents through one secure shared fax number.</p>
        </div>
        <button className="mini-btn" type="button" onClick={loadFaxes} disabled={loading}>
          <i className="ti ti-refresh" /> Refresh
        </button>
      </header>

      {error ? <p className="form-error efax-page-error">{error}</p> : null}

      <section className="efax-summary-grid" aria-label="Fax summary">
        <div className="efax-summary-item">
          <i className="ti ti-printer" aria-hidden="true" />
          <div><span>Shared fax number</span><strong>{sharedFaxNumber || "Not configured"}</strong></div>
        </div>
        <div className="efax-summary-item">
          <i className="ti ti-inbox" aria-hidden="true" />
          <div><span>Inbox</span><strong>{inboxFaxes.length} fax{inboxFaxes.length === 1 ? "" : "es"}</strong></div>
        </div>
        <div className="efax-summary-item">
          <i className="ti ti-send" aria-hidden="true" />
          <div><span>Outbox</span><strong>{outboxFaxes.length} fax{outboxFaxes.length === 1 ? "" : "es"}</strong></div>
        </div>
      </section>

      <nav className="efax-primary-tabs" role="tablist" aria-label="eFax sections">
        <button
          className={activeActivityTab === "outbox" ? "active" : ""}
          type="button"
          role="tab"
          id="efax-tab-outbox"
          aria-selected={activeActivityTab === "outbox"}
          aria-controls="efax-panel-outbox"
          onClick={() => selectTab("outbox")}
        >
          <i className="ti ti-send" /> Outbox <span>{outboxFaxes.length}</span>
        </button>
        <button
          className={activeActivityTab === "inbox" ? "active" : ""}
          type="button"
          role="tab"
          id="efax-tab-inbox"
          aria-selected={activeActivityTab === "inbox"}
          aria-controls="efax-panel-inbox"
          onClick={() => selectTab("inbox")}
        >
          <i className="ti ti-inbox" /> Inbox <span>{inboxFaxes.length}</span>
        </button>
        <button
          className={activeActivityTab === "send" ? "active" : ""}
          type="button"
          role="tab"
          id="efax-tab-send"
          aria-selected={activeActivityTab === "send"}
          aria-controls="efax-panel-send"
          onClick={() => selectTab("send")}
        >
          <i className="ti ti-file-upload" /> Send Fax
        </button>
      </nav>

      {activeActivityTab === "send" ? (
      <div className="efax-operations-grid" role="tabpanel" id="efax-panel-send" aria-labelledby="efax-tab-send">
        <Panel title="Send Fax">
        <div className="efax-shared-sender">
          <div className="efax-shared-sender-copy">
            <span>OCarely shared fax number</span>
            <strong>{sharedFaxNumber || "Not configured"}</strong>
            <small>Used automatically for sending and receiving workspace faxes.</small>
          </div>
          <div className={`efax-sender-usage ${manualFaxesRemaining === 0 ? "limit-reached" : ""}`}>
            <span>Manual sends this month</span>
            <strong>{manualFaxesSentThisMonth} / {MONTHLY_MANUAL_FAX_LIMIT}</strong>
            <small>{manualFaxesRemaining} remaining</small>
          </div>
        </div>
        <form className="assessment-drawer-form" onSubmit={sendFax}>
          <div className="efax-compose-grid">
            <section className="efax-compose-section efax-compose-recipients">
              <div className="efax-compose-section-head">
                <i className="ti ti-address-book" aria-hidden="true" />
                <div>
                  <h3>Recipients</h3>
                  <p>Add one or more destination fax numbers.</p>
                </div>
              </div>
            <div className="ncp-form-field efax-recipient-field">
              <span>Destination numbers</span>
              <div className="efax-recipient-list">
                {(form.faxNumbers?.length ? form.faxNumbers : [form.faxNumber || ""]).map((faxNumber, index) => (
                  <div className="efax-recipient-row" key={`fax-recipient-${index}`}>
                    <input
                      inputMode="tel"
                      type="tel"
                      placeholder={index === 0 ? "+1 (425) 555-0123" : "Add another fax number"}
                      value={faxNumber}
                      onChange={(event) => updateFaxRecipient(index, event.target.value)}
                      onPaste={(event) => handleFaxRecipientPaste(index, event)}
                     />
                     {(form.faxNumbers?.length || 0) > 1 ? (
                       <button className="mini-btn efax-recipient-remove" type="button" onClick={() => removeFaxRecipient(index)} aria-label="Remove fax number">
                         <i className="ti ti-x" />
                       </button>
                     ) : null}
                   </div>
                 ))}
              </div>
              {sharedFaxNumber ? (
                <small className="efax-recipient-hint">
                  Enter the destination fax number. The shared workspace number above is used as the sender.
                </small>
              ) : null}
              <button className="mini-btn efax-add-recipient" type="button" onClick={addFaxRecipient}>
                <i className="ti ti-plus" /> Add number
              </button>
              </div>
            </section>

            <section className="efax-compose-section efax-compose-content">
              <div className="efax-compose-section-head">
                <i className="ti ti-file-description" aria-hidden="true" />
                <div>
                  <h3>Fax content</h3>
                  <p>Upload a document or type a message to send.</p>
                </div>
              </div>
              <div className="efax-content-controls">
                <div className="ncp-form-field efax-mode-field">
                  <span>Send type</span>
                  <div className="efax-mode-toggle" role="tablist" aria-label="Fax send type">
                    <button className={sendMode === "document" ? "active" : ""} type="button" onClick={() => switchSendMode("document")}>
                      <i className="ti ti-file-upload" /> Document
                    </button>
                    <button className={sendMode === "text" ? "active" : ""} type="button" onClick={() => switchSendMode("text")}>
                      <i className="ti ti-message-2" /> Text
                    </button>
                  </div>
                </div>
                <label className="ncp-form-field">
                  <span>File name</span>
                  <input
                    className="efax-readonly-field"
                    value={firstText(form.fileName, selectedFileName)}
                    placeholder={sendMode === "text" ? "fax-message.pdf" : "Selected document name"}
                    readOnly
                    aria-readonly="true"
                  />
                </label>
              </div>
              {sendMode === "document" ? (
                <label className="ncp-form-field efax-file-field">
                  <span>Document</span>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.txt,.doc,.docx" onChange={handleFileChange} />
                </label>
              ) : (
                <label className="ncp-form-field efax-text-field">
                  <span>Message</span>
                  <textarea
                    value={form.textMessage}
                    onChange={(event) => updateForm("textMessage", event.target.value)}
                    placeholder="Type the text that should be sent as fax pages."
                    rows={7}
                  />
                </label>
              )}
              {attachedDocument || uploadStatus !== "idle" ? (
                <div className={`efax-document-strip ${uploadStatus}`}>
                  <div className="efax-document-icon">
                    <i className={`ti ${uploadStatus === "uploading" ? "ti-loader-2" : uploadStatus === "error" ? "ti-alert-circle" : "ti-file-description"}`} />
                  </div>
                  <div className="efax-document-meta">
                    <strong>{attachedDocument?.name || selectedFileName || "Fax document"}</strong>
                    <span>
                      {uploadStatus === "uploading" ? "Uploading document..." : null}
                      {uploadStatus === "ready" ? `Ready to send${attachedDocument?.size ? ` - ${formatFileSize(attachedDocument.size)}` : ""}` : null}
                      {uploadStatus === "error" ? uploadError : null}
                    </span>
                    {attachedDocument?.fileId ? <small>File ID: {attachedDocument.fileId}</small> : null}
                  </div>
                  {uploadStatus !== "uploading" ? (
                    <button className="mini-btn efax-document-remove" type="button" onClick={clearDocument}>
                      <i className="ti ti-x" /> Remove
                    </button>
                  ) : null}
                </div>
              ) : null}
            </section>
          </div>
          <div className="assessment-drawer-actions">
            <button className="mini-btn efax-clear-button" type="button" onClick={clearForm}>
              Clear
            </button>
            <button className="mini-btn efax-send-button" type="submit" disabled={!canSendFax || manualFaxesRemaining === 0 || sending || uploading || uploadStatus === "uploading"}>
              {sending ? "Sending..." : uploading ? "Uploading..." : "Send Fax"}
            </button>
          </div>
        </form>
        </Panel>
      </div>
      ) : (

      <div role="tabpanel" id={`efax-panel-${activeActivityTab}`} aria-labelledby={`efax-tab-${activeActivityTab}`}>
      <Panel title={activeActivityTab === "inbox" ? "Inbox" : "Outbox"}>
        <div className="efax-activity-toolbar">
          <p>{activeActivityTab === "inbox" ? "Faxes received by this workspace." : "Faxes sent from this workspace."}</p>
          <button className="mini-btn" type="button" onClick={loadFaxes} disabled={loading}>
            <i className="ti ti-refresh" /> Refresh Activity
          </button>
        </div>
        <div className="efax-activity-list">
          {renderFaxRows()}
        </div>
      </Panel>
      </div>
      )}
    </CareShell>
  );
}
